import { Menu, X } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItemType } from 'components/Breadcrumbs';
import { Sidebar } from 'components/Sidebar';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

export const DashboardPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on mobile by default
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate breadcrumb items based on the current path
  const generateBreadcrumbItems = (): BreadcrumbItemType[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems: BreadcrumbItemType[] = [{ label: 'Dashboard', path: '/' }];

    let currentPath = '';

    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;

      // Check if the segment looks like an ID (MongoDB ObjectId or UUID)
      const isId =
        /^[0-9a-f]{24}$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        );

      let label;
      if (isId) {
        // For IDs, show a shortened version
        label = `ID: ${segment.substring(0, 8)}...`;
      } else {
        // For normal segments, capitalize and format
        label = segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }

      breadcrumbItems.push({
        label,
        path: currentPath,
        isId,
      });
    });

    return breadcrumbItems;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleSidebar}
          className="rounded-md bg-white shadow-md"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 pt-20 lg:pt-6">
            <Breadcrumbs items={generateBreadcrumbItems()} className="mb-6" />
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};
